import React from 'react';
import { useTranslation } from 'react-i18next';
import type * as monacoEditor from 'monaco-editor';
import { BeforeMount, Monaco, OnMount } from '@monaco-editor/react';

import CodeEditor from '../CodeEditor';
import { SCHEMA_FILE_NAME } from '../CodeEditor/const';
import { flattenErrorMessages, localizeModelMarkers, setupMarkersObservation } from '../CodeEditor/utils';
import { CodeEditorStatus } from '../CodeEditor/types';

import { JSON_META_SCHEMA, JSON_META_SCHEMAS, JSON_SCHEMA_URL } from './const';

export interface ISchemaEditorProps {
  value: string;
  isFullscreen: boolean;
  readOnly?: boolean;
  codeFolder?: string;
  options?: monacoEditor.editor.IStandaloneEditorConstructionOptions;
  onBlur?: () => void;
  onChange?: (value?: string) => void;
  onValidate?: (errors: string[], status: CodeEditorStatus, value?: string) => void;
}

const SchemaEditor: React.FC<ISchemaEditorProps> = ({
  value,
  isFullscreen,
  readOnly = false,
  codeFolder,
  options,
  onBlur,
  onChange,
  onValidate,
}) => {
  const { t, i18n } = useTranslation();

  const beforeEditorMount: BeforeMount = (monaco: Monaco) => {
    const diagnosticsOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions || {};
    if (diagnosticsOptions.schemas?.some((info) => info.uri === JSON_SCHEMA_URL)) {
      return;
    }

    const schema = JSON_META_SCHEMAS[i18n.resolvedLanguage || i18n.language] || JSON_META_SCHEMA;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      ...diagnosticsOptions,
      validate: true,
      schemas: [
        ...(diagnosticsOptions.schemas || []),
        {
          uri: JSON_SCHEMA_URL,
          fileMatch: [`*/${SCHEMA_FILE_NAME}`],
          schema: flattenErrorMessages(schema, t),
        },
      ],
    });
  };

  const onEditorMount: OnMount = (editor, monaco) => {
    setupMarkersObservation(editor, monaco, (markers) => localizeModelMarkers(markers, t));
  };

  return (
    <CodeEditor
      beforeEditorMount={beforeEditorMount}
      codeLanguage="json"
      codeUrl={`file://${codeFolder || '-'}/${SCHEMA_FILE_NAME}`}
      isFullscreen={isFullscreen}
      options={options}
      readOnly={readOnly}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onEditorMount={onEditorMount}
      onValidate={onValidate}
    />
  );
};

export default SchemaEditor;
